import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import { scrollToHashOrTop } from '../utils/scroll';
import { useAuth } from '../context/AuthContext';

// Labels map to section ids on the homepage (src/pages/index.js)
const NAV_LINKS = [
  { label: 'Home', hash: '#top' },
  { label: 'About', hash: '#about' },
  { label: 'Our Program', hash: '#drills' },
  { label: 'Why Us', hash: '#why-us' },
  { label: 'Gear', hash: '#gear' },
];

export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logout();
    } finally {
      navigate('/');
    }
  };

  const activeHash =
    location.pathname === '/'
      ? location.hash || '#top'
      : '';

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const goToSection = (hash) => (event) => {
    event.preventDefault();
    setMenuOpen(false);

    if (location.pathname !== '/') {
      navigate(`/${hash}`);
      return;
    }

    if (location.hash !== hash) {
      navigate(`/${hash}`, { replace: true });
    }

    // Defer so route/hash updates settle, then scroll under the fixed nav.
    window.requestAnimationFrame(() => scrollToHashOrTop(hash));
  };

  return (
    <header className="nav" id="nav">
      <div className="container nav__inner">
        <Link to="/#top" className="nav__brand" onClick={goToSection('#top')}>
          <img src={logo} alt="" className="nav__mark" />
          <span className="nav__brand-text">
            <span className="nav__word">RANGE DRILLS</span>
            <span className="nav__tagline">Shoot Better. Hit Your Mark.</span>
          </span>
        </Link>

        <nav className={`nav__links${menuOpen ? ' is-open' : ''}`} id="navLinks">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={`/${link.hash}`}
              className={activeHash === link.hash ? 'is-active' : undefined}
              onClick={goToSection(link.hash)}
            >
              {link.label}
            </Link>
          ))}
          {user && (
            <Link
              to="/portal"
              className={location.pathname === '/portal' ? 'is-active' : undefined}
              onClick={() => setMenuOpen(false)}
            >
              Portal
            </Link>
          )}
        </nav>

        <div className="nav__cta">
          {user ? (
            <button type="button" className="btn btn--ghost btn--sm" onClick={handleLogout}>
              Logout
            </button>
          ) : (
            <Link to="/login" className="btn btn--ghost btn--sm" onClick={() => setMenuOpen(false)}>
              Login
            </Link>
          )}
          <Link
            to="/#download"
            className="btn btn--primary btn--sm"
            onClick={goToSection('#download')}
          >
            Let&apos;s Shoot
          </Link>
        </div>

        <button
          type="button"
          className="nav__toggle"
          id="navToggle"
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span /><span /><span />
        </button>
      </div>
    </header>
  );
}
